import { SWRConfiguration } from "swr";

import { FetcherResult } from "../api-client/fetcher";
import { ModifiedSWRResponse, useSwrGet } from "../api-client/useSwrGet";
import { useCriteria } from "../blocks/Accessories/Filters/Criteria/useCriteria";
import { useTireSeason } from "../blocks/Tires/Results/Filters/TireSeasonFilters/useTireSeason";
import { AnyPartType, PartTypePackage } from "../constants/partTypes";
import { LanguageMap, useLanguage } from "../i18n/useLanguage";
import { DealerVehicleResultsRoute } from "../navigation/DealerRoutes";
import { useRouteParams } from "../navigation/useRouteParams";
import { getPartName } from "../util/getPartName";
import { useGetVehicleInfo } from "./useGetVehicleInfo";
import {
  TireSize,
  VehicleHasMappedParts,
} from "./useGetVehicleInfoByVehicleKeys";
import {
  PartPricingItem,
  PromotionLink,
  StockItem,
  TirePartItem,
} from "./useSearchTiresByVehicle";
import { WheelPartItem } from "./useSearchWheelsByVehicle";

export const useSearchPartPackagesByVehicle = (
  swrOptions: SWRConfiguration<FetcherResult<Payload>> = {}
): ModifiedSWRResponse<FetcherResult<Payload>, Payload> => {
  const { data: vehicleInfo } = useGetVehicleInfo();
  const route = useRouteParams(DealerVehicleResultsRoute);
  const shouldFetch = Boolean(route && vehicleInfo?.vehicleId);
  const [season] = useTireSeason();
  const [criteria] = useCriteria();
  const [language] = useLanguage();

  const { data, ...result } = useSwrGet<Payload>(
    shouldFetch
      ? "/retailSiteWebService/parts/partPackage/searchPartPackagesByVehicle.php"
      : null,
    {
      dealerId: route?.dealerId || "",
      isNational: "0",
      season: String(season),
      vehicleId: String(vehicleInfo?.vehicleId || ""),
    },
    swrOptions
  );

  if (!data) {
    return result as ModifiedSWRResponse<FetcherResult<Payload>, Payload>;
  }

  const filteredData = data.filter((part) => {
    const isVisible = !part.partSummary.primaryPart.hideItem;
    let matched = true;
    if (criteria.length) {
      const name = getPartName(part, language);
      matched = name.toLowerCase().includes(criteria.toLowerCase());
    }
    return isVisible && matched;
  });

  return { ...result, data: filteredData };
};

type Payload = PackagePartItem[];

export type PackagePartItem = {
  partDetails: {
    primaryPart: PrimaryPackagePart;
    relatedParts: PackageItem[];
    secondaryParts: PrimaryPackagePart[];
  };
  partDynamicName: LanguageMap;
  partPricing: {
    primaryPart: PackagePartPricing;
    relatedParts: PackagePartPricing[];
    secondaryParts: PackagePartPricing[];
  };
  partStock: {
    primaryPart: StockItem[];
    relatedParts: StockItem[];
    secondaryParts: StockItem[];
  };
  partSummary: {
    primaryPart: PackageSummaryPrimaryPart;
    relatedParts: PackageItem[];
    secondaryParts: PackageItem[];
  };
  partTire?: TirePartItem;
  partVisualizerAsset: string[];
  partWheel?: WheelPartItem;
};

export type PackageSummaryPrimaryPart = {
  hideItem: boolean;
  itemDescription: LanguageMap;
  itemHasDealerPricing: boolean;
  itemHasStock: boolean;
  itemId: string;
  itemImageLink: string;
  itemImageSecondaryLinkList: string[];
  itemNationalPartNumber: string;
  itemOriginalEquipmentFlag: string;
  itemPartNumber: string;
  itemPartOption: null | unknown;
  itemShippingMetrics: null | unknown;
  itemTitle: LanguageMap;
  itemType: PartTypePackage;
  supportConsumerOrder: boolean;
  supportDealerOrder: boolean;
  vehicleDetails: Record<string, VehicleMappingRowItem>;
  vehicleIdList: number[];
};

export type VehicleMappingRowItem = {
  vehicleChassisNumber: string;
  vehicleFullTireSizeInfo: TireSize[];
  vehicleFullWheelDiameterInfo: string[];
  vehicleHasMappedParts: VehicleHasMappedParts;
  vehicleId: string;
  vehicleManufacturerId: string;
  vehicleManufacturerName: string;
  vehicleModelId: string;
  vehicleModelImage: string;
  vehicleModelName: LanguageMap;
  vehicleOriginalTiresize: TireSize;
  vehicleRequiresRunFlatTires: boolean;
  vehicleSubmodelId: string;
  vehicleSubmodelImage?: string;
  vehicleSubmodelName: LanguageMap;
  vehicleVisualizerForegroundImageUrl: string;
  vehicleVisualizerShadowImageUrl: string;
  vehicleYear: string;
  vehicleYearId: string;
};

export type PrimaryPackagePart = {
  expireDate: string;
  itemId: string;
  itemType: PartTypePackage;
  itemWarning: LanguageMap;
  packageDescription: LanguageMap;
  packageImageLink: string;
  packageItem: PackageItem[];
  packageItemHasRunFlat: boolean;
  packageLineItem: unknown[];
  packageManufacturerId: string;
  packageManufacturerName: string;
  packageName: LanguageMap;
  packagePartNumber: string;
  releaseDate: string;
};

export type PackageItem = {
  itemDescription: LanguageMap;
  itemId: string;
  itemPackageLineItem: unknown[];
  itemPartDescription: LanguageMap;
  itemPartNumber: string;
  itemQuantity: string;
  itemTitle: LanguageMap;
  itemType: AnyPartType;
  itemTypeDescription: LanguageMap;
};

export type PackagePartPricing = {
  advancedPrice?: number;
  itemId: string;
  itemType: PartTypePackage;
  link: [] | PromotionLink;
  price: {
    [key: string]: PartPricingItem;
  };
  priceLastUpdated: string;
  prioritizeInSearch: boolean;
};

export type ResultParameters = {
  dealerId: number;
  originalEquipmentFilter: string;
  season: number;
  vehicleId: number;
  vehicleSubmodelTireSizeId: number;
};
